import React, { useEffect, useState, useRef } from 'react';
import { profitAPI } from '../services/api';
import toast from 'react-hot-toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const INR = n => '₹' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const HRS = n => `${n.toFixed(1)}h`;

// ── Mini sparkline ──────────────────────────────────────────────────────────────
const Spark = ({ values, color = 'var(--pm-primary)', height = 40 }) => {
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 120, h = height;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  const area = `0,${h} ${pts} ${w},${h}`;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#sg-${color.replace('#','')})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};

// ── Bar chart ───────────────────────────────────────────────────────────────────
const BarChart = ({ data, keys, colors, labels, height = 200 }) => {
  const max = Math.max(...data.flatMap(d => keys.map(k => d[k] || 0)), 1);
  const barW = Math.floor(320 / (data.length * keys.length + data.length));
  return (
    <svg width="100%" height={height + 30} viewBox={`0 0 ${Math.max(400, data.length * (keys.length * (barW + 2) + 12))} ${height + 30}`} preserveAspectRatio="none">
      {data.map((d, i) => {
        const gx = i * (keys.length * (barW + 2) + 12);
        return (
          <g key={i}>
            {keys.map((k, ki) => {
              const bh = ((d[k] || 0) / max) * height;
              const x = gx + ki * (barW + 2);
              return (
                <g key={k}>
                  <rect x={x} y={height - bh} width={barW} height={bh} fill={colors[ki]} rx={2} opacity={0.85}/>
                  <title>{labels[ki]}: {INR(d[k] || 0)}</title>
                </g>
              );
            })}
            <text x={gx + (keys.length * (barW + 2)) / 2} y={height + 20} textAnchor="middle" fontSize={9} fill="#94a3b8">{d.label}</text>
          </g>
        );
      })}
      {/* Y gridlines */}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <line key={f} x1={0} y1={height - f * height} x2="100%" y2={height - f * height} stroke="#f1f5f9" strokeWidth={1}/>
      ))}
    </svg>
  );
};

// ── Delta badge ─────────────────────────────────────────────────────────────────
const DeltaBadge = ({ d, invert }) => {
  if (!d || d.pct === null) return null;
  const good = invert ? !d.up : d.up;
  return (
    <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:6, background: good ? '#d1fae5' : '#fee2e2', color: good ? '#065f46' : '#991b1b' }}>
      {d.up ? '↑' : '↓'} {Math.abs(d.pct)}%
    </span>
  );
};

// ── KPI card ─────────────────────────────────────────────────────────────────────
const KPI = ({ label, value, sub, spark, color, delta, invertDelta, icon }) => (
  <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 16px #0000000a', border:'1px solid #f1f5f9', borderTop:`3px solid ${color}` }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
      <div>
        <div style={{ fontSize:12, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>{icon} {label}</div>
        <div style={{ fontSize:28, fontWeight:800, color:'#0f172a', lineHeight:1 }}>{value}</div>
        {sub && <div style={{ fontSize:12, color:'#64748b', marginTop:6 }}>{sub}</div>}
        <div style={{ marginTop:8 }}><DeltaBadge d={delta} invert={invertDelta}/></div>
      </div>
      {spark && <div style={{ opacity:0.7 }}>{spark}</div>}
    </div>
  </div>
);

// ── Donut ───────────────────────────────────────────────────────────────────────
const Donut = ({ slices, size = 120 }) => {
  const total = slices.reduce((s, sl) => s + sl.value, 0) || 1;
  let angle = -90;
  const cx = size / 2, cy = size / 2, r = size * 0.38, sw = size * 0.13;
  return (
    <svg width={size} height={size}>
      {slices.map((sl, i) => {
        const pct = sl.value / total;
        const sweep = pct * 360;
        const sa = (angle * Math.PI) / 180; angle += sweep;
        const ea = (angle * Math.PI) / 180;
        const x1 = cx + r * Math.cos(sa), y1 = cy + r * Math.sin(sa);
        const x2 = cx + r * Math.cos(ea), y2 = cy + r * Math.sin(ea);
        const large = sweep > 180 ? 1 : 0;
        if (sweep < 0.5) return null;
        return <path key={i} d={`M${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2}`} fill="none" stroke={sl.color} strokeWidth={sw} strokeLinecap="butt"><title>{sl.label}: {Math.round(pct * 100)}%</title></path>;
      })}
      <circle cx={cx} cy={cy} r={r - sw / 2} fill="white"/>
    </svg>
  );
};

export default function ProfitAnalytics() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange]     = useState(6);
  const [tab, setTab]         = useState('overview'); // overview | employees | projects | timelogs
  const [selMonth, setSelMonth] = useState(null); // index into data

  useEffect(() => { load(); }, [range]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await profitAPI.getMonthly(range);
      const d = r.data.data || [];
      setData(d);
      setSelMonth(d.length - 1);
    } catch(e) { toast.error('Failed to load analytics'); }
    setLoading(false);
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:48, height:48, border:'4px solid #f1f5f9', borderTop:'4px solid #6366f1', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }}/>
        <div style={{ color:'#94a3b8', fontSize:14, fontFamily:"'Sora',sans-serif" }}>Loading financial data...</div>
      </div>
    </div>
  );

  if (!data.length) return (
    <div style={{ textAlign:'center', padding:80, color:'#94a3b8', fontFamily:"'Sora',sans-serif" }}>
      <div style={{ fontSize:48, marginBottom:16 }}>📊</div>
      <h2 style={{ color:'#475569' }}>No financial data yet</h2>
      <p>Generate payslips and log billable time to see profit analytics.</p>
    </div>
  );

  const cur = data[selMonth ?? data.length - 1] || data[data.length - 1];
  const prev = data[(selMonth ?? data.length - 1) - 1];

  const delta = (cur, prev, key) => {
    if (!prev) return null;
    const diff = (cur[key] || 0) - (prev[key] || 0);
    const pct = prev[key] ? ((diff / prev[key]) * 100).toFixed(1) : null;
    return { diff, pct, up: diff >= 0 };
  };

  const revDelta    = delta(cur.financials, prev?.financials, 'revenue');
  const expDelta    = delta(cur.financials, prev?.financials, 'expenses');
  const profitDelta = delta(cur.financials, prev?.financials, 'profit');
  const salDelta    = delta(cur.salary, prev?.salary, 'total');

  const profitColor = cur.financials.profit >= 0 ? '#10b981' : '#ef4444';

  const S = {
    page: { maxWidth:1400, margin:'0 auto', fontFamily:"'Sora',sans-serif", paddingBottom:40 },
    card: { background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 16px #0000000a', border:'1px solid #f1f5f9' },
    tab: (a) => ({ padding:'8px 20px', borderRadius:8, border:'none', fontWeight:600, fontSize:13, cursor:'pointer', transition:'all .2s', background: a ? 'var(--pm-gradient)' : '#f8fafc', color: a ? '#fff' : '#64748b' }),
    range: (a) => ({ padding:'6px 14px', borderRadius:8, border:'none', fontWeight:600, fontSize:12, cursor:'pointer', background: a ? '#0f172a' : '#f1f5f9', color: a ? '#fff' : '#64748b' }),
    th: { padding:'11px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'2px solid #f1f5f9', whiteSpace:'nowrap' },
    td: { padding:'12px 14px', fontSize:13, borderBottom:'1px solid #f8fafc', color:'#475569' },
  };

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');
        @keyframes spin { to { transform:rotate(360deg) } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
        .pa-row:hover { background:#f8fafc!important; }
        .pa-month-btn:hover { background:#e2e8f0!important; }
        .pa-month-btn.active { background:#0f172a!important; color:#fff!important; }
      `}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ margin:0, fontSize:30, fontWeight:800, color:'#0f172a' }}>💹 Profit Analytics</h1>
          <p style={{ margin:'6px 0 0', color:'#64748b', fontSize:14 }}>Revenue, expenses, salary costs & actual profit by month</p>
        </div>
        <div style={{ display:'flex', gap:6, background:'#f1f5f9', padding:4, borderRadius:10 }}>
          {[3,6,9,12].map(n => (
            <button key={n} style={S.range(range===n)} onClick={() => setRange(n)}>{n}M</button>
          ))}
        </div>
      </div>

      {/* Month selector timeline */}
      <div style={{ display:'flex', gap:6, marginBottom:24, overflowX:'auto', paddingBottom:4 }}>
        {data.map((d, i) => {
          const isProfit = d.financials.profit >= 0;
          return (
            <button key={i} className={`pa-month-btn ${selMonth === i ? 'active' : ''}`}
              onClick={() => setSelMonth(i)}
              style={{ flexShrink:0, padding:'10px 16px', borderRadius:10, border:'none', cursor:'pointer', textAlign:'center', transition:'all .2s', background: selMonth === i ? '#0f172a' : '#fff', color: selMonth === i ? '#fff' : '#475569', boxShadow:'0 1px 4px #0000001a', minWidth:80 }}>
              <div style={{ fontSize:11, fontWeight:600 }}>{d.label}</div>
              <div style={{ fontSize:10, marginTop:3, color: selMonth === i ? (isProfit ? '#6ee7b7' : '#fca5a5') : (isProfit ? '#10b981' : '#ef4444'), fontWeight:700 }}>
                {isProfit ? '▲' : '▼'} {Math.abs(d.financials.profitMargin)}%
              </div>
            </button>
          );
        })}
      </div>

      {/* KPI Strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16, marginBottom:24, animation:'fadeUp .4s ease' }}>
        <KPI label="Total Revenue" value={INR(cur.financials.revenue)} icon="💰"
          sub={`Billable: ${INR(cur.time.billableRevenue)} + Pipeline: ${INR(cur.projects.revenue)}`}
          color="var(--pm-primary)" delta={revDelta}
          spark={<Spark values={data.map(d => d.financials.revenue)} color="var(--pm-primary)"/>}/>
        <KPI label="Total Expenses" value={INR(cur.financials.expenses)} icon="💸"
          sub={`Salary: ${INR(cur.salary.total)} + Ops: ${INR(cur.projects.expenses)}`}
          color="#ef4444" delta={expDelta} invertDelta
          spark={<Spark values={data.map(d => d.financials.expenses)} color="#ef4444"/>}/>
        <KPI label={cur.financials.profit >= 0 ? 'Net Profit' : 'Net Loss'} value={INR(cur.financials.profit)} icon={cur.financials.profit >= 0 ? '📈' : '📉'}
          sub={`Margin: ${cur.financials.profitMargin}%`}
          color={profitColor} delta={profitDelta}
          spark={<Spark values={data.map(d => d.financials.profit)} color={profitColor}/>}/>
        <KPI label="Salary Costs" value={INR(cur.salary.total)} icon="👥"
          sub={`${cur.salary.count} employees · Net paid: ${INR(cur.salary.netPaid)}`}
          color="#f59e0b" delta={salDelta} invertDelta
          spark={<Spark values={data.map(d => d.salary.total)} color="#f59e0b"/>}/>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, background:'#f1f5f9', padding:4, borderRadius:10, marginBottom:20, width:'fit-content' }}>
        {[['overview','📊 Overview'],['employees','👥 Employees'],['projects','📁 Projects'],['timelogs','⏱ Time Worked']].map(([v,l]) => (
          <button key={v} style={S.tab(tab===v)} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ──────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div style={{ animation:'fadeUp .3s ease' }}>
          {/* Revenue vs Expenses bar chart */}
          <div style={{ ...S.card, marginBottom:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>Revenue vs Expenses vs Profit</h3>
              <div style={{ display:'flex', gap:16 }}>
                {[['Revenue','var(--pm-primary)'],['Expenses','#ef4444'],['Profit','#10b981']].map(([l,c]) => (
                  <div key={l} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#64748b' }}>
                    <div style={{ width:10, height:10, borderRadius:2, background:c }}/>
                    {l}
                  </div>
                ))}
              </div>
            </div>
            <BarChart
              data={data.map(d => ({ label:d.label, revenue:d.financials.revenue, expenses:d.financials.expenses, profit:Math.max(d.financials.profit,0) }))}
              keys={['revenue','expenses','profit']}
              colors={['var(--pm-primary)','#ef4444','#10b981']}
              labels={['Revenue','Expenses','Profit']}
              height={180}/>
          </div>

          {/* Two columns: Breakdown + Month detail */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>

            {/* Cost breakdown donut */}
            <div style={S.card}>
              <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Cost Breakdown — {cur.label}</h3>
              <div style={{ display:'flex', alignItems:'center', gap:24 }}>
                <Donut size={140} slices={[
                  { label:'Salary', value:cur.salary.total, color:'#f59e0b' },
                  { label:'Operations', value:cur.projects.expenses, color:'#ef4444' },
                  { label:'Deductions', value:cur.salary.deductions, color:'var(--pm-secondary)' },
                ]}/>
                <div style={{ flex:1 }}>
                  {[
                    ['Salary Cost', INR(cur.salary.total), '#f59e0b'],
                    ['Operations', INR(cur.projects.expenses), '#ef4444'],
                    ['Deductions', INR(cur.salary.deductions), 'var(--pm-secondary)'],
                  ].map(([l,v,c]) => (
                    <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
                        <div style={{ width:10, height:10, borderRadius:'50%', background:c }}/>
                        <span style={{ color:'#64748b' }}>{l}</span>
                      </div>
                      <span style={{ fontWeight:700, color:'#0f172a', fontSize:13 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Revenue breakdown */}
            <div style={S.card}>
              <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Revenue Breakdown — {cur.label}</h3>
              <div style={{ display:'flex', alignItems:'center', gap:24 }}>
                <Donut size={140} slices={[
                  { label:'Billable Time', value:cur.time.billableRevenue, color:'var(--pm-primary)' },
                  { label:'Project Pipeline', value:cur.projects.revenue, color:'#10b981' },
                ]}/>
                <div style={{ flex:1 }}>
                  {[
                    ['Billable Time', INR(cur.time.billableRevenue), 'var(--pm-primary)', `${HRS(cur.time.billableHours)} logged`],
                    ['Project Pipeline', INR(cur.projects.revenue), '#10b981', `${cur.projects.pipeline} active projects`],
                  ].map(([l,v,c,s]) => (
                    <div key={l} style={{ marginBottom:14 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
                          <div style={{ width:10, height:10, borderRadius:'50%', background:c }}/>
                          <span style={{ color:'#64748b' }}>{l}</span>
                        </div>
                        <span style={{ fontWeight:700, color:'#0f172a', fontSize:13 }}>{v}</span>
                      </div>
                      <div style={{ fontSize:11, color:'#94a3b8', marginTop:2, marginLeft:18 }}>{s}</div>
                    </div>
                  ))}
                  <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid #f1f5f9' }}>
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <span style={{ fontSize:13, fontWeight:700, color:'#475569' }}>Billable Utilisation</span>
                      <span style={{ fontWeight:800, color:'var(--pm-primary)' }}>
                        {(cur.time.billableHours + cur.time.nonBillableHours) > 0
                          ? `${((cur.time.billableHours / (cur.time.billableHours + cur.time.nonBillableHours)) * 100).toFixed(0)}%`
                          : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profit margin trend */}
          <div style={S.card}>
            <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Profit Margin Trend</h3>
            <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:100 }}>
              {data.map((d, i) => {
                const m = d.financials.profitMargin;
                const pos = m >= 0;
                const h = Math.min(Math.abs(m), 100);
                return (
                  <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ fontSize:9, fontWeight:700, color: pos ? '#10b981' : '#ef4444' }}>{m}%</div>
                    <div style={{ width:'100%', height:80, display:'flex', flexDirection:'column', justifyContent:'center', position:'relative' }}>
                      <div style={{ position:'absolute', top:'50%', left:0, right:0, height:1, background:'#e2e8f0' }}/>
                      {pos
                        ? <div style={{ position:'absolute', bottom:'50%', left:2, right:2, height:`${h/2}%`, background:'linear-gradient(180deg,#10b981,#6ee7b7)', borderRadius:'3px 3px 0 0', minHeight:2 }}/>
                        : <div style={{ position:'absolute', top:'50%', left:2, right:2, height:`${h/2}%`, background:'linear-gradient(180deg,#fca5a5,#ef4444)', borderRadius:'0 0 3px 3px', minHeight:2 }}/>
                      }
                    </div>
                    <div style={{ fontSize:9, color:'#94a3b8', textAlign:'center' }}>{d.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── EMPLOYEES TAB ─────────────────────────────────────────────────────── */}
      {tab === 'employees' && (
        <div style={{ animation:'fadeUp .3s ease' }}>
          {/* Salary cost by month bar */}
          <div style={{ ...S.card, marginBottom:20 }}>
            <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Monthly Salary Costs</h3>
            <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:120 }}>
              {data.map((d, i) => {
                const max = Math.max(...data.map(x => x.salary.total), 1);
                const h = (d.salary.total / max) * 100;
                const isSel = selMonth === i;
                return (
                  <div key={i} onClick={() => setSelMonth(i)} style={{ flex:1, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:'#f59e0b' }}>{d.salary.count > 0 ? INR(d.salary.total) : ''}</div>
                    <div style={{ width:'100%', height:100, display:'flex', alignItems:'flex-end' }}>
                      <div style={{ width:'100%', height:`${h}%`, background: isSel ? 'linear-gradient(180deg,#f59e0b,#fbbf24)' : '#fef3c7', borderRadius:'4px 4px 0 0', minHeight:2, transition:'all .3s', border: isSel ? '2px solid #f59e0b' : '1px solid #fde68a' }}/>
                    </div>
                    <div style={{ fontSize:9, color:'#94a3b8' }}>{d.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Employee table for selected month */}
          <div style={S.card}>
            <h3 style={{ margin:'0 0 4px', fontSize:15, fontWeight:700 }}>Employee Payroll — {cur.label}</h3>
            <p style={{ margin:'0 0 16px', fontSize:13, color:'#94a3b8' }}>Salary breakdown, attendance & deductions per employee</p>
            {cur.employeeBreakdown.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px 20px', color:'#94a3b8' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>💼</div>
                <div style={{ fontWeight:600 }}>No payslips generated for {cur.label}</div>
                <div style={{ fontSize:12, marginTop:4 }}>Generate payslips from the Salary page</div>
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#f8fafc' }}>
                      {['Employee','Role','Gross Salary','Deductions','Net Salary','Days Present','Absent','Overtime Hrs','Status'].map(h => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cur.employeeBreakdown.map((e, i) => (
                      <tr key={i} className="pa-row" style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ ...S.td, fontWeight:700, color:'#0f172a' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13, flexShrink:0 }}>
                              {e.name.charAt(0)}
                            </div>
                            {e.name}
                          </div>
                        </td>
                        <td style={S.td}>{e.position || '—'}</td>
                        <td style={{ ...S.td, fontWeight:700, color:'#0f172a' }}>{INR(e.grossSalary)}</td>
                        <td style={{ ...S.td, color:'#ef4444' }}>{INR(e.grossSalary - e.netSalary)}</td>
                        <td style={{ ...S.td, fontWeight:800, color:'#10b981', fontSize:14 }}>{INR(e.netSalary)}</td>
                        <td style={S.td}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ width:60, height:6, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                              <div style={{ height:'100%', width:`${e.workingDays ? (e.presentDays / e.workingDays) * 100 : 0}%`, background:'#10b981', borderRadius:4 }}/>
                            </div>
                            <span style={{ fontWeight:600 }}>{e.presentDays}/{e.workingDays}</span>
                          </div>
                        </td>
                        <td style={{ ...S.td, color: e.absentDays > 3 ? '#ef4444' : '#475569', fontWeight: e.absentDays > 3 ? 700 : 400 }}>{e.absentDays}</td>
                        <td style={{ ...S.td, color: e.overtimeHrs > 0 ? 'var(--pm-primary)' : '#94a3b8', fontWeight: e.overtimeHrs > 0 ? 700 : 400 }}>{e.overtimeHrs > 0 ? `${e.overtimeHrs}h` : '—'}</td>
                        <td style={S.td}>
                          <span style={{ padding:'3px 10px', borderRadius:10, fontSize:11, fontWeight:700,
                            background: e.status === 'paid' ? '#d1fae5' : e.status === 'approved' ? '#dbeafe' : '#fef3c7',
                            color: e.status === 'paid' ? '#065f46' : e.status === 'approved' ? '#1e40af' : '#92400e' }}>
                            {e.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr style={{ background:'#f8fafc', borderTop:'2px solid #e2e8f0' }}>
                      <td style={{ ...S.td, fontWeight:800, color:'#0f172a' }} colSpan={2}>TOTAL ({cur.salary.count} employees)</td>
                      <td style={{ ...S.td, fontWeight:800, color:'#0f172a' }}>{INR(cur.salary.total)}</td>
                      <td style={{ ...S.td, fontWeight:800, color:'#ef4444' }}>{INR(cur.salary.deductions)}</td>
                      <td style={{ ...S.td, fontWeight:800, color:'#10b981', fontSize:15 }}>{INR(cur.salary.netPaid)}</td>
                      <td colSpan={4} style={S.td}/>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PROJECTS TAB ──────────────────────────────────────────────────────── */}
      {tab === 'projects' && (
        <div style={{ animation:'fadeUp .3s ease' }}>
          {/* Pipeline vs Completed */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:20 }}>
            {[
              { label:'Pipeline Projects', value:cur.projects.pipeline, color:'var(--pm-primary)', icon:'🚀', sub:'active + planning' },
              { label:'Completed Projects', value:cur.projects.completed, color:'#10b981', icon:'✅', sub:'this month' },
              { label:'Total Project Budget', value:INR(cur.projects.revenue), color:'var(--pm-secondary)', icon:'💼', sub:'contracted value' },
              { label:'Project Expenses', value:INR(cur.projects.expenses), color:'#ef4444', icon:'💸', sub:'amount spent' },
            ].map(k => (
              <div key={k.label} style={{ ...S.card, borderLeft:`4px solid ${k.color}` }}>
                <div style={{ fontSize:24, marginBottom:8 }}>{k.icon}</div>
                <div style={{ fontSize:26, fontWeight:800, color:'#0f172a' }}>{k.value}</div>
                <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{k.label}</div>
                <div style={{ fontSize:11, color:k.color, fontWeight:600, marginTop:4 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Project pipeline trend over months */}
          <div style={{ ...S.card, marginBottom:20 }}>
            <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Project Pipeline & Revenue Trend</h3>
            <BarChart
              data={data.map(d => ({ label:d.label, pipeline:d.projects.revenue, expenses:d.projects.expenses }))}
              keys={['pipeline','expenses']}
              colors={['var(--pm-primary)','#ef4444']}
              labels={['Project Revenue','Project Expenses']}
              height={160}/>
            <div style={{ display:'flex', gap:20, marginTop:12 }}>
              {[['Project Revenue','var(--pm-primary)'],['Project Expenses','#ef4444']].map(([l,c]) => (
                <div key={l} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#64748b' }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:c }}/>{l}
                </div>
              ))}
            </div>
          </div>

          {/* Monthly project summary table */}
          <div style={S.card}>
            <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700 }}>Monthly Project Summary</h3>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Month','Active Projects','Pipeline Value','Expenses','Net Project P&L','Completed'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((d, i) => {
                  const pl = d.projects.revenue - d.projects.expenses;
                  return (
                    <tr key={i} className="pa-row" onClick={() => setSelMonth(i)}
                      style={{ background: selMonth === i ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafbfc', cursor:'pointer' }}>
                      <td style={{ ...S.td, fontWeight:700, color:'#0f172a' }}>{d.label}</td>
                      <td style={S.td}>{d.projects.pipeline}</td>
                      <td style={{ ...S.td, fontWeight:600 }}>{INR(d.projects.revenue)}</td>
                      <td style={{ ...S.td, color:'#ef4444' }}>{INR(d.projects.expenses)}</td>
                      <td style={{ ...S.td, fontWeight:700, color: pl >= 0 ? '#10b981' : '#ef4444' }}>
                        {pl >= 0 ? '+' : ''}{INR(pl)}
                      </td>
                      <td style={S.td}>{d.projects.completed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TIME WORKED TAB ───────────────────────────────────────────────────── */}
      {tab === 'timelogs' && (
        <div style={{ animation:'fadeUp .3s ease' }}>
          {/* Summary KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:20 }}>
            {[
              { label:'Billable Hours', value:HRS(cur.time.billableHours), color:'var(--pm-primary)', icon:'⏱', sub:`${INR(cur.time.billableRevenue)} revenue` },
              { label:'Non-Billable Hours', value:HRS(cur.time.nonBillableHours), color:'#94a3b8', icon:'🕰', sub:'internal work' },
              { label:'Total Hours Logged', value:HRS(cur.time.billableHours + cur.time.nonBillableHours), color:'#f59e0b', icon:'📊', sub:'all employees' },
              { label:'Revenue per Hour', value: cur.time.billableHours > 0 ? INR(cur.time.billableRevenue / cur.time.billableHours) : '—', color:'#10b981', icon:'💰', sub:'avg billable rate' },
            ].map(k => (
              <div key={k.label} style={{ ...S.card, borderLeft:`4px solid ${k.color}` }}>
                <div style={{ fontSize:24, marginBottom:8 }}>{k.icon}</div>
                <div style={{ fontSize:26, fontWeight:800, color:'#0f172a' }}>{k.value}</div>
                <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{k.label}</div>
                <div style={{ fontSize:11, color:k.color, fontWeight:600, marginTop:4 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Hours trend */}
          <div style={{ ...S.card, marginBottom:20 }}>
            <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Billable vs Non-Billable Hours Trend</h3>
            <BarChart
              data={data.map(d => ({ label:d.label, billable:d.time.billableHours, nonBillable:d.time.nonBillableHours }))}
              keys={['billable','nonBillable']}
              colors={['var(--pm-primary)','#e2e8f0']}
              labels={['Billable Hours','Non-Billable Hours']}
              height={160}/>
          </div>

          {/* Per-employee hours table */}
          <div style={S.card}>
            <h3 style={{ margin:'0 0 4px', fontSize:15, fontWeight:700 }}>Hours per Employee — {cur.label}</h3>
            <p style={{ margin:'0 0 16px', fontSize:13, color:'#94a3b8' }}>From approved time logs only</p>
            {cur.hoursByEmployee.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px 20px', color:'#94a3b8' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>⏱</div>
                <div style={{ fontWeight:600 }}>No approved time logs for {cur.label}</div>
                <div style={{ fontSize:12, marginTop:4 }}>Approve time logs from Time Approval page</div>
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#f8fafc' }}>
                    {['Employee','Billable Hours','Non-Billable Hours','Total Hours','Billable %','Estimated Revenue'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cur.hoursByEmployee.sort((a,b) => b.total - a.total).map((e, i) => {
                    const billPct = e.total > 0 ? ((e.billable / e.total) * 100).toFixed(0) : 0;
                    const estRate = cur.time.billableHours > 0 ? cur.time.billableRevenue / cur.time.billableHours : 0;
                    return (
                      <tr key={i} className="pa-row" style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ ...S.td, fontWeight:700, color:'#0f172a' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13 }}>
                              {e.name.charAt(0)}
                            </div>
                            {e.name}
                          </div>
                        </td>
                        <td style={{ ...S.td, fontWeight:600, color:'var(--pm-primary)' }}>{HRS(e.billable)}</td>
                        <td style={{ ...S.td, color:'#94a3b8' }}>{HRS(e.nonBillable)}</td>
                        <td style={{ ...S.td, fontWeight:700 }}>{HRS(e.total)}</td>
                        <td style={S.td}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ width:60, height:6, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                              <div style={{ height:'100%', width:`${billPct}%`, background: billPct >= 70 ? '#10b981' : billPct >= 50 ? '#f59e0b' : '#ef4444', borderRadius:4 }}/>
                            </div>
                            <span style={{ fontWeight:600, color: billPct >= 70 ? '#10b981' : billPct >= 50 ? '#f59e0b' : '#ef4444' }}>{billPct}%</span>
                          </div>
                        </td>
                        <td style={{ ...S.td, fontWeight:600, color:'#10b981' }}>{INR(e.billable * estRate)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ background:'#f8fafc', borderTop:'2px solid #e2e8f0' }}>
                    <td style={{ ...S.td, fontWeight:800, color:'#0f172a' }}>TOTAL</td>
                    <td style={{ ...S.td, fontWeight:800, color:'var(--pm-primary)' }}>{HRS(cur.time.billableHours)}</td>
                    <td style={{ ...S.td, fontWeight:800, color:'#94a3b8' }}>{HRS(cur.time.nonBillableHours)}</td>
                    <td style={{ ...S.td, fontWeight:800 }}>{HRS(cur.time.billableHours + cur.time.nonBillableHours)}</td>
                    <td style={S.td}/>
                    <td style={{ ...S.td, fontWeight:800, color:'#10b981' }}>{INR(cur.time.billableRevenue)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Bottom: full monthly P&L table */}
      {tab === 'overview' && (
        <div style={{ ...S.card, marginTop:20 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700 }}>Full Monthly P&L Statement</h3>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Month','Revenue','Salary Cost','Project Exp.','Total Expenses','Gross Profit','Margin','Employees Paid','Billable Hours'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((d, i) => {
                  const profit = d.financials.profit;
                  return (
                    <tr key={i} className="pa-row" onClick={() => setSelMonth(i)}
                      style={{ background: selMonth === i ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafbfc', cursor:'pointer' }}>
                      <td style={{ ...S.td, fontWeight:700, color:'#0f172a' }}>{d.label}</td>
                      <td style={{ ...S.td, fontWeight:600 }}>{INR(d.financials.revenue)}</td>
                      <td style={{ ...S.td, color:'#f59e0b', fontWeight:600 }}>{INR(d.salary.total)}</td>
                      <td style={{ ...S.td, color:'#ef4444' }}>{INR(d.projects.expenses)}</td>
                      <td style={{ ...S.td, fontWeight:600 }}>{INR(d.financials.expenses)}</td>
                      <td style={{ ...S.td, fontWeight:800, color: profit >= 0 ? '#10b981' : '#ef4444', fontSize:14 }}>
                        {profit >= 0 ? '+' : ''}{INR(profit)}
                      </td>
                      <td style={S.td}>
                        <span style={{ fontWeight:700, color: d.financials.profitMargin >= 20 ? '#10b981' : d.financials.profitMargin >= 0 ? '#f59e0b' : '#ef4444' }}>
                          {d.financials.profitMargin}%
                        </span>
                      </td>
                      <td style={S.td}>{d.salary.count}</td>
                      <td style={S.td}>{HRS(d.time.billableHours)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
